import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CircleNotch, Play, Stop, ArrowClockwise, Trash, Copy, DownloadSimple, Desktop, Plus, Users, X } from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;
const T = '#14B8A6';

const ST = {
  queued: 'text-slate-300 border-white/10', creating_network: 'text-amber-400 border-amber-400/30',
  creating_vm: 'text-amber-400 border-amber-400/30', configuring_users: 'text-blue-400 border-blue-400/30',
  running: 'text-[#14B8A6] border-[#14B8A6]/40', failed: 'text-red-400 border-red-400/40',
};

const RDPMachines = () => {
  const [vms, setVms] = useState([]);
  const [busy, setBusy] = useState('');
  const [userModal, setUserModal] = useState(null);
  const [newUser, setNewUser] = useState('');
  const pollRef = useRef(null);

  const load = async () => {
    try { const r = await axios.get(`${API}/api/rdp/vms`, { withCredentials: true }); setVms(r.data); }
    catch { /* ignore */ }
  };
  useEffect(() => {
    load();
    pollRef.current = setInterval(load, 6000);
    return () => clearInterval(pollRef.current);
  }, []);

  const copy = (t) => { navigator.clipboard.writeText(t); toast.success('Copied'); };

  const power = async (id, action) => {
    setBusy(id + action);
    try { await axios.post(`${API}/api/rdp/vms/${id}/power/${action}`, {}, { withCredentials: true }); toast.success(`${action} done`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || `${action} failed`); }
    finally { setBusy(''); }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this machine and ALL its Azure resources? This cannot be undone.')) return;
    setBusy(id + 'del');
    try { await axios.delete(`${API}/api/rdp/vms/${id}`, { withCredentials: true }); toast.success('Deleted'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
    finally { setBusy(''); }
  };

  const downloadRdp = (id, name) => {
    axios.get(`${API}/api/rdp/vms/${id}/rdp-file`, { withCredentials: true, responseType: 'blob' })
      .then(r => { const url = URL.createObjectURL(r.data); const a = document.createElement('a'); a.href = url; a.download = `${name}.rdp`; a.click(); URL.revokeObjectURL(url); })
      .catch(e => toast.error(e.response?.data?.detail || 'No IP yet'));
  };

  const addUser = async () => {
    if (!newUser.trim()) return;
    setBusy('adduser');
    try {
      const r = await axios.post(`${API}/api/rdp/vms/${userModal.id}/users`, { users: [{ username: newUser.trim() }] }, { withCredentials: true });
      toast.success(`User created: ${r.data.users[0].username} / ${r.data.users[0].password}`);
      setNewUser(''); setUserModal(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setBusy(''); }
  };

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-2 duration-500">
      <div>
        <h1 className="text-4xl font-black mb-2" style={{ color: T }}>MY MACHINES</h1>
        <p className="text-slate-400 font-mono">Your Windows RDP machines. Start/stop to control cost, manage users, connect.</p>
      </div>

      <div className="grid gap-4">
        {vms.map(v => {
          const provisioning = ['queued', 'creating_network', 'creating_vm', 'configuring_users'].includes(v.status);
          const running = v.power_state === 'running';
          return (
            <div key={v.id} className="bg-card border border-white/10 p-6" data-testid={`rdp-vm-${v.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="flex items-start gap-3">
                  <Desktop size={26} style={{ color: T }} className="mt-1" />
                  <div>
                    <div className="font-bold text-white text-lg">{v.name}</div>
                    <div className="text-xs text-slate-500 font-mono">{v.account_label} · {v.region} · {v.vm_size} · {v.os_disk_gb}GB</div>
                    {v.public_ip && <div className="text-sm font-mono mt-1 flex items-center gap-2" style={{ color: T }}>{v.public_ip}:3389 <button onClick={() => copy(`${v.public_ip}`)}><Copy size={13} className="text-slate-400 hover:text-white" /></button></div>}
                  </div>
                </div>
                <div className="flex flex-col items-end gap-1">
                  <span className={`px-2 py-1 text-xs font-bold border ${ST[v.status] || 'text-slate-300 border-white/10'}`}>
                    {provisioning ? <span className="flex items-center gap-1"><CircleNotch size={12} className="animate-spin" /> {v.status}</span> : v.status}
                  </span>
                  {v.power_state && v.status === 'running' && <span className="text-[10px] text-slate-500 font-mono">power: {v.power_state}</span>}
                </div>
              </div>

              {v.status === 'failed' && v.error_msg && <div className="mt-3 text-xs text-red-400 font-mono bg-red-500/5 border border-red-500/20 p-2">{v.error_msg}</div>}
              {v.users_warning && <div className="mt-3 text-xs text-amber-400 font-mono">user setup warning: {v.users_warning}</div>}

              {/* credentials */}
              {v.status === 'running' && (
                <div className="mt-4 bg-[#0A0B0E] border border-white/10 p-3">
                  <div className="text-xs uppercase tracking-widest text-slate-500 font-bold mb-2">RDP Credentials</div>
                  <div className="grid md:grid-cols-2 gap-2 text-sm">
                    <div className="flex items-center gap-2"><span className="text-slate-500 w-20">admin</span><span className="font-mono text-white">{v.admin_user}</span><button onClick={() => copy(v.admin_user)}><Copy size={13} className="text-slate-400 hover:text-white" /></button></div>
                    <div className="flex items-center gap-2"><span className="text-slate-500 w-20">password</span><span className="font-mono text-white truncate max-w-[180px]">{v.admin_password}</span><button onClick={() => copy(v.admin_password)}><Copy size={13} className="text-slate-400 hover:text-white" /></button></div>
                  </div>
                  {v.users && v.users.length > 0 && (
                    <div className="mt-2 pt-2 border-t border-white/10">
                      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1">Extra users</div>
                      {v.users.map(u => (
                        <div key={u.username} className="flex items-center gap-2 text-xs font-mono text-slate-300">
                          {u.username} : <span className="text-white">{u.password}</span>
                          <button onClick={() => copy(`${u.username} / ${u.password}`)}><Copy size={12} className="text-slate-400 hover:text-white" /></button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* actions */}
              <div className="mt-4 flex flex-wrap gap-2">
                {v.status === 'running' && (
                  <>
                    <button onClick={() => power(v.id, running ? 'stop' : 'start')} disabled={busy.startsWith(v.id)} data-testid={`rdp-power-${v.id}`}
                      className="flex items-center gap-1 border border-white/15 px-3 py-1.5 font-bold text-xs uppercase tracking-wider hover:bg-white/5 disabled:opacity-50">
                      {busy === v.id + (running ? 'stop' : 'start') ? <CircleNotch size={14} className="animate-spin" /> : (running ? <Stop size={14} /> : <Play size={14} />)}
                      {running ? 'Stop (save $)' : 'Start'}
                    </button>
                    <button onClick={() => power(v.id, 'restart')} disabled={busy.startsWith(v.id)}
                      className="flex items-center gap-1 border border-white/15 px-3 py-1.5 font-bold text-xs uppercase tracking-wider hover:bg-white/5 disabled:opacity-50">
                      {busy === v.id + 'restart' ? <CircleNotch size={14} className="animate-spin" /> : <ArrowClockwise size={14} />} Restart
                    </button>
                    <button onClick={() => downloadRdp(v.id, v.name)} className="flex items-center gap-1 border px-3 py-1.5 font-bold text-xs uppercase tracking-wider hover:bg-[#14B8A6] hover:text-black transition-colors" style={{ borderColor: T + '66', color: T }}>
                      <DownloadSimple size={14} /> .rdp
                    </button>
                    <button onClick={() => setUserModal(v)} data-testid={`rdp-manage-users-${v.id}`} className="flex items-center gap-1 border border-white/15 px-3 py-1.5 font-bold text-xs uppercase tracking-wider hover:bg-white/5">
                      <Users size={14} /> Add User
                    </button>
                  </>
                )}
                <button onClick={() => del(v.id)} disabled={busy.startsWith(v.id)} data-testid={`rdp-delete-${v.id}`}
                  className="flex items-center gap-1 bg-red-500/10 text-red-400 border border-red-500/20 px-3 py-1.5 font-bold text-xs uppercase tracking-wider hover:bg-red-500 hover:text-white transition-colors disabled:opacity-50">
                  {busy === v.id + 'del' ? <CircleNotch size={14} className="animate-spin" /> : <Trash size={14} />} Delete
                </button>
              </div>
            </div>
          );
        })}
        {vms.length === 0 && <div className="text-slate-500 text-center py-10">No machines yet.</div>}
      </div>

      {userModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setUserModal(null)}>
          <div className="bg-card border p-6 w-full max-w-md" style={{ borderColor: T + '55' }} onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-bold text-white">Add RDP user to {userModal.name}</h3>
              <button onClick={() => setUserModal(null)}><X size={18} className="text-slate-400" /></button>
            </div>
            <input value={newUser} onChange={e => setNewUser(e.target.value)} placeholder="username" data-testid="rdp-new-username"
              className="w-full bg-[#0A0B0E] border border-white/10 p-3 text-white focus:outline-none focus:border-[#14B8A6] mb-4" />
            <button onClick={addUser} disabled={busy === 'adduser'} data-testid="rdp-confirm-add-user"
              className="w-full flex items-center justify-center gap-2 text-black py-3 font-bold uppercase tracking-wider disabled:opacity-50" style={{ background: T }}>
              {busy === 'adduser' ? <CircleNotch size={18} className="animate-spin" /> : <Plus size={18} />} Create User (auto password)
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default RDPMachines;
